import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';

const TermsAndConditions = () => {
  return (
    <div className="min-h-screen bg-[#F2EEE7]">
      {/* Navigation */}
      <nav className="border-b border-[#D4CFC7] bg-[#F2EEE7]/95 backdrop-blur-sm sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-6 py-4">
          <Link to="/" className="inline-flex items-center gap-2 text-[#5C4033] hover:text-[#7A1E2C] transition-colors">
            <ArrowLeft className="w-4 h-4" />
            Back to Home
          </Link>
        </div>
      </nav>

      <div className="max-w-4xl mx-auto px-6 py-16">
        <h1 className="text-4xl font-serif font-bold text-[#5C4033] mb-4">Terms of Service</h1>
        <p className="text-[#6B5B4F] mb-12">Last updated: 25 March 2026</p>

        <div className="prose prose-lg max-w-none text-[#5C4033]">
          <p className="text-[#6B5B4F] leading-relaxed mb-8">
            These Terms of Service ("Terms") govern your access to and use of the Sharedequal website and services. By accessing or using the Sharedequal platform, you agree to be bound by these Terms. If you do not agree to these Terms, you must not use the Sharedequal platform.
          </p>

          <h2 className="text-2xl font-serif font-bold text-[#5C4033] mt-12 mb-4">1. Definitions</h2>
          <p className="text-[#6B5B4F] leading-relaxed mb-4">In these Terms, unless the context otherwise requires:</p>
          <ul className="list-disc pl-6 text-[#6B5B4F] space-y-2 mb-8">
            <li>"Sharedequal" refers to Sharedequal and its operators.</li>
            <li>"User" refers to any individual who accesses or uses the Sharedequal platform.</li>
            <li>"Drop" refers to a shared shipment arrangement organised through Sharedequal, whereby multiple Users participate in a consolidated order from the same brand or supplier.</li>
            <li>"Platform" refers to the Sharedequal website and any related services.</li>
          </ul>

          <h2 className="text-2xl font-serif font-bold text-[#5C4033] mt-12 mb-4">2. Nature of Services</h2>
          <p className="text-[#6B5B4F] leading-relaxed mb-4">
            Sharedequal acts solely as a facilitator of shared shipment arrangements. Sharedequal does not sell, manufacture, distribute, store, inspect, or warrant any products. All products are purchased directly by Users from third-party brands or suppliers.
          </p>
          <p className="text-[#6B5B4F] leading-relaxed mb-8">
            Sharedequal does not assume any responsibility or liability in respect of product quality, defects, sizing, fitness for purpose, warranties, or returns, all of which remain the sole responsibility of the relevant brand or supplier.
          </p>

          <h2 className="text-2xl font-serif font-bold text-[#5C4033] mt-12 mb-4">3. Eligibility</h2>
          <p className="text-[#6B5B4F] leading-relaxed mb-8">
            By using the Platform, you represent and warrant that you are at least eighteen (18) years of age and have the legal capacity to enter into binding agreements.
          </p>

          <h2 className="text-2xl font-serif font-bold text-[#5C4033] mt-12 mb-4">4. Drops and Participation</h2>
          <p className="text-[#6B5B4F] leading-relaxed mb-8">
            A User may request to initiate or join a Drop. Participation in a Drop does not guarantee that the Drop will proceed. Sharedequal reserves the right, in its sole discretion, to cancel, delay, or decline any Drop, including where there is insufficient participation or for operational reasons.
          </p>

          <h2 className="text-2xl font-serif font-bold text-[#5C4033] mt-12 mb-4">5. User Obligations</h2>
          <p className="text-[#6B5B4F] leading-relaxed mb-4">Each User undertakes to:</p>
          <ul className="list-disc pl-6 text-[#6B5B4F] space-y-2 mb-4">
            <li>Provide complete, accurate, and correct information in respect of item specifications, sizes, and other details;</li>
            <li>Review and confirm order details prior to confirmation;</li>
            <li>Comply with all payment deadlines communicated by Sharedequal; and</li>
            <li>Act in good faith towards Sharedequal and other Users participating in the same Drop.</li>
          </ul>
          <p className="text-[#6B5B4F] leading-relaxed mb-8">
            Sharedequal shall not be responsible for any loss, delay, or cost arising from inaccurate or incomplete information provided by a User.
          </p>

          <h2 className="text-2xl font-serif font-bold text-[#5C4033] mt-12 mb-4">6. Fees and Payments</h2>
          <p className="text-[#6B5B4F] leading-relaxed mb-4">
            Sharedequal charges a service fee per user per drop as follows:
          </p>
          <ul className="list-disc pl-6 text-[#6B5B4F] space-y-2 mb-4">
            <li>Other items: $8 per user</li>
            <li>Oversized items (e.g. mallets): $12 per user</li>
            <li>If a user's order includes both oversized and non-oversized items, the fee charged will be $8</li>
          </ul>
          <p className="text-[#6B5B4F] leading-relaxed mb-4">
            This fee will be communicated to users once they have confirmed their orders.
          </p>
          <p className="text-[#6B5B4F] leading-relaxed mb-8">
            Shipping costs, customs duties, taxes, and other logistics-related charges shall be shared among Users in accordance with the allocation methodology applicable to the relevant Drop. Final amounts may vary based on courier invoices and customs assessments.
          </p>

          <h2 className="text-2xl font-serif font-bold text-[#5C4033] mt-12 mb-4">7. Deposits and Cancellations</h2>
          <p className="text-[#6B5B4F] leading-relaxed mb-4">
            Upon confirmation of a Drop and placement of the order with the brand or supplier, any deposits paid by Users shall be non-refundable. This reflects Sharedequal's commitment on behalf of all participating Users.
          </p>
          <p className="text-[#6B5B4F] leading-relaxed mb-8">
            A User who withdraws after confirmation may remain liable for costs already incurred in connection with the Drop.
          </p>

          <h2 className="text-2xl font-serif font-bold text-[#5C4033] mt-12 mb-4">8. Shipping and Delivery</h2>
          <p className="text-[#6B5B4F] leading-relaxed mb-4">
            Any timelines provided in relation to shipping or delivery are estimates only. Sharedequal does not guarantee delivery dates. Delays may occur due to circumstances beyond Sharedequal's control, including supplier delays, customs clearance, courier issues, or force majeure events.
          </p>
          <p className="text-[#6B5B4F] leading-relaxed mb-8">
            Sharedequal shall not be liable for delays attributable to third parties.
          </p>

          <h2 className="text-2xl font-serif font-bold text-[#5C4033] mt-12 mb-4">9. Collection</h2>
          <p className="text-[#6B5B4F] leading-relaxed mb-8">
            Collection arrangements, including locations and timeframes, shall be communicated in relation to each Drop. Users are responsible for collecting their items within the specified timeframe. Sharedequal shall not be responsible for items not collected within such timeframe.
          </p>

          <h2 className="text-2xl font-serif font-bold text-[#5C4033] mt-12 mb-4">10. Product Issues</h2>
          <p className="text-[#6B5B4F] leading-relaxed mb-8">
            Any disputes, claims, or issues relating to product defects, quality, sizing, or warranties must be resolved directly between the User and the relevant brand or supplier. Sharedequal may, at its discretion, assist with communication but does not assume responsibility for the resolution of such matters.
          </p>

          <h2 className="text-2xl font-serif font-bold text-[#5C4033] mt-12 mb-4">11. Limitation of Liability</h2>
          <p className="text-[#6B5B4F] leading-relaxed mb-4">
            To the maximum extent permitted by law, Sharedequal shall not be liable for any indirect, incidental, consequential, or special losses arising out of or in connection with the use of the Platform or participation in a Drop.
          </p>
          <p className="text-[#6B5B4F] leading-relaxed mb-8">
            Sharedequal's aggregate liability, if any, shall be limited to the platform fees paid by the User in respect of the relevant Drop.
          </p>

          <h2 className="text-2xl font-serif font-bold text-[#5C4033] mt-12 mb-4">12. Termination</h2>
          <p className="text-[#6B5B4F] leading-relaxed mb-8">
            Sharedequal reserves the right to suspend or terminate a User's access to the Platform at any time if the User breaches these Terms, acts in bad faith, or interferes with the operation of the Platform.
          </p>

          <h2 className="text-2xl font-serif font-bold text-[#5C4033] mt-12 mb-4">13. Amendments</h2>
          <p className="text-[#6B5B4F] leading-relaxed mb-8">
            Sharedequal may amend these Terms from time to time. Updated Terms will be posted on the Platform with a revised effective date. Continued use of the Platform constitutes acceptance of the amended Terms.
          </p>

          <h2 className="text-2xl font-serif font-bold text-[#5C4033] mt-12 mb-4">14. Governing Law</h2>
          <p className="text-[#6B5B4F] leading-relaxed mb-8">
            These Terms shall be governed by and construed in accordance with the laws of Singapore.
          </p>

          <h2 className="text-2xl font-serif font-bold text-[#5C4033] mt-12 mb-4">15. Contact</h2>
          <p className="text-[#6B5B4F] leading-relaxed mb-8">
            For any queries relating to these Terms, please contact us at{' '}
            <a href="mailto:sharedequal@gmail.com" className="text-[#7A1E2C] hover:underline">sharedequal@gmail.com</a>
            {' '}or via our{' '}
            <Link to="/contact" className="text-[#7A1E2C] hover:underline">Contact page</Link>.
          </p>
        </div>

        <div className="mt-12 pt-8 border-t border-[#D4CFC7]">
          <Link to="/" className="inline-flex items-center gap-2 text-[#7A1E2C] hover:underline font-medium">
            <ArrowLeft className="w-4 h-4" />
            Back to Home
          </Link>
        </div>
      </div>
    </div>
  );
};

export default TermsAndConditions;
